import { ChangeDetectionStrategy, Component, computed, inject, model, OnInit, signal } from '@angular/core';
import { CarouselModule, OwlOptions } from 'ngx-owl-carousel-o';
import { ImagesService } from '../../services/images/images.service';
import { WebConfigService } from '../../services/web-config/web-config.service';

@Component({
  selector: 'app-carousel',
  imports: [CarouselModule],
  template: `
    @if (displayCarousel()) {
      <owl-carousel-o [options]="customOptions">
        @for (image of images(); track $index) {
          <ng-template carouselSlide [id]="image.src + '-' + $index">
            <img class="carousel-img w-full! h-full!" [src]="image.src" [alt]="image.title" [title]="image.title" />
          </ng-template>
        }
      </owl-carousel-o>
    }
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
    .owl-carousel-o {
      width: 100%;
      height: 100%;
    }
    .owl-stage-outer {
      height: 100%;
    }
    .owl-stage {
      height: 100%;
      display: flex !important;
    }
    .owl-item {
      height: 100%;
    }
    .carousel-slide {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    img.carousel-img {
      object-fit: contain;
      max-height: 100vh !important;
      max-width: 100vw !important;
      width: 100%;
      height: 100%;
      object-position: center;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class CarouselComponent implements OnInit {
  #webconfig = inject(WebConfigService);
  #imagesService = inject(ImagesService);
  items = model<string[] | { src: string; title?: string }[]>([]);
  displayCarousel = signal<boolean>(true);
  images = computed(() => this.#calImages(this.items()));

  customOptions: OwlOptions = {
    loop: true,
    margin: 0,
    nav: false,
    autoplay: true,
    autoplayTimeout: 7000,
    autoplayHoverPause: true,
    autoHeight: false,
    autoWidth: false,
    autoplaySpeed: 700,
    dots: false,
    responsive: {
      0: {
        items: 1,
      },
      600: {
        items: 1,
      },
    },
  };

  ngOnInit(): void {
    const { assets } = this.#webconfig;
    this.#imagesService.checkImageExistsHead(assets.authScreenBackground).then((exists) => {
      if (!exists && assets.carouselImages) {
        const image = assets.carouselImages;
        this.#setDefaultImage(image);
      } else if (exists || assets.authScreenBackground === ImagesService.background) {
        this.displayCarousel.set(true);
      } else {
        this.displayCarousel.set(false);
      }
    });
  }

  #calImages(pictures: string[] | { src: string; title?: string }[]): { src: string; title?: string }[] {
    const items = pictures?.length ? pictures : this.#setCarouselImages();
    const images = items.map((item) => (typeof item === 'string' ? { src: item } : item));
    return images.length === 2 ? [...images, ...images] : images;
  }

  #setCarouselImages(): string[] {
    if (this.#webconfig.assets.carouselImages && this.#webconfig.assets.carouselImages.length > 0) {
      return this.#webconfig.assets.carouselImages;
    } else {
      return [this.#webconfig.assets.authScreenBackground];
    }
  }

  #setDefaultImage(defaultImg: string | string[] = ImagesService.slides) {
    this.items.set(Array.isArray(defaultImg) ? defaultImg : [defaultImg]);
  }
}
